/**
 * Step-up on demand: a sensitive call answers STEP_UP_REQUIRED → a dialog asks for the second factor (or, for accounts
 * without one, the password and / or a fresh confirmation in our Telegram bot — ADR-0036, ADR-0046) → the call is
 * retried once. Valid 5 minutes on the server.
 *
 *   const withStepUp = useStepUp();
 *   await withStepUp(() => post("/me/mfa/recovery-codes"));
 */
import type { Me, MfaMethod } from "@prop/contracts";
import { Button, Field, Input, Notice } from "@prop/ui";
import { createContext, type ReactNode, useCallback, useContext, useRef, useState } from "react";
import { useAuthT, useSecurityT } from "../../i18n";
import { ApiError, post } from "../../lib/api";
import { useMe, useSetMe } from "../../lib/session";
import { useAction } from "../../lib/useAction";
import { SecondFactorForm } from "../auth/SecondFactor";
import { OrDivider, TelegramButton, type TelegramDone } from "../auth/Telegram";

type Ctx = <T>(fn: () => Promise<T>) => Promise<T>;
const StepUpCtx = createContext<Ctx>((fn) => fn());

export function useStepUp(): Ctx {
  return useContext(StepUpCtx);
}

export function StepUpProvider({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false);
  const waiter = useRef<{ resolve: () => void; reject: (e: unknown) => void } | null>(null);

  const withStepUp = useCallback<Ctx>(async (fn) => {
    try {
      return await fn();
    } catch (err) {
      if (!(err instanceof ApiError) || err.code !== "STEP_UP_REQUIRED") throw err;
      await new Promise<void>((resolve, reject) => {
        waiter.current = { resolve, reject };
        setOpen(true);
      });
      return fn();
    }
  }, []);

  const close = (ok: boolean) => {
    setOpen(false);
    if (ok) waiter.current?.resolve();
    else waiter.current?.reject(new ApiError(403, "STEP_UP_REQUIRED", ""));
    waiter.current = null;
  };

  return (
    <StepUpCtx.Provider value={withStepUp}>
      {children}
      {open && <StepUpDialog onDone={() => close(true)} onCancel={() => close(false)} />}
    </StepUpCtx.Provider>
  );
}

function StepUpDialog({ onDone, onCancel }: { onDone: () => void; onCancel: () => void }) {
  const t = useSecurityT();
  const ta = useAuthT();
  const { data: me } = useMe();
  const setMe = useSetMe();
  const [password, setPassword] = useState("");
  const methods: MfaMethod[] = me?.mfa.factors.length
    ? [...new Set(me.mfa.factors.map((f) => f.type as MfaMethod)), ...(me.mfa.recoveryCodesLeft ? (["recovery"] as const) : [])]
    : [];
  const pw = useAction(async () => {
    setMe(await post<Me>("/auth/step-up", { method: "password", password }));
    onDone();
  });
  // A bot request of this account, confirmed in Telegram (ADR-0046) → the step-up takes it.
  const telegramDone = async (d: TelegramDone) => {
    if (d.kind !== "confirmed") return;
    setMe(await post<Me>("/auth/step-up", { method: "telegram", id: d.id, secret: d.secret }));
    onDone();
  };
  const withPassword = me?.user.hasPassword ?? true;
  const withTelegram = !!me?.user.telegram;

  return (
    <div role="dialog" aria-modal="true" aria-labelledby="stepup-title" className="fixed inset-0 z-modal flex items-center justify-center bg-black/55 p-4 backdrop-blur-sm">
      <section className="glass-panel flex max-h-[calc(100dvh-2rem)] w-full max-w-sm flex-col gap-5 overflow-y-auto overscroll-contain rounded-[14px] p-6 shadow-2xl">
        <header>
          <h2 id="stepup-title" className="font-display text-2xl font-semibold uppercase leading-none tracking-[-0.03em] [font-stretch:120%]">
            {t("stepUp.title")}
          </h2>
          <p className="mt-2 text-sm text-muted-foreground">{t("stepUp.hint")}</p>
        </header>
        {methods.length ? (
          <SecondFactorForm
            methods={methods}
            optionsPath="/auth/step-up/webauthn/options"
            submitLabel={t("stepUp.confirm")}
            onAnswer={async (answer) => {
              setMe(await post<Me>("/auth/step-up", answer));
              onDone();
            }}
          />
        ) : (
          <>
            {withPassword && (
              <form
                className="flex flex-col gap-4"
                onSubmit={(e) => {
                  e.preventDefault();
                  void pw.run();
                }}
              >
                <Field label={ta("password")} htmlFor="stepup-pw">
                  <Input id="stepup-pw" type="password" autoComplete="current-password" autoFocus value={password} onChange={(e) => setPassword(e.target.value)} />
                </Field>
                {pw.message && <Notice tone="error">{pw.message}</Notice>}
                <Button type="submit" size="lg" disabled={pw.pending || !password}>
                  {t("stepUp.confirm")}
                </Button>
              </form>
            )}
            {withPassword && withTelegram && <OrDivider />}
            {withTelegram && (
              <>
                {!withPassword && <p className="text-sm text-muted-foreground">{t("stepUp.telegramHint")}</p>}
                <TelegramButton purpose="step_up" onDone={telegramDone} />
              </>
            )}
          </>
        )}
        <Button type="button" variant="ghost" onClick={onCancel}>
          {t("stepUp.cancel")}
        </Button>
      </section>
    </div>
  );
}
